import { EmptyChartState } from "@/components/common/EmptyChartState";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import type { LaseredDataPoint } from "@/types";
import { Divider, Box, Typography } from "@mui/material";
import { currencyCompactNotation, formatUSD } from "@/utils/formatters/currencyFormatter";
import { useAppTheme } from "@/hooks/useAppTheme";
import { getChartTooltipStyle, getCartesianGridProps } from "../utils/chartStyles";

interface LaseredImpactChartProps {
  data: LaseredDataPoint[];
}

export const LaseredImpactChart = ({ data: rawData }: LaseredImpactChartProps) => {
  const { theme } = useAppTheme();

  // Group by client_name, sum/average across carriers for the same client
  const grouped: Record<string, { totalDeductible: number; totalLasered: number }> = {};

  for (const d of rawData) {
    if (d.lasered_count <= 0) continue;
    if (!grouped[d.client_name]) {
      grouped[d.client_name] = { totalDeductible: 0, totalLasered: 0 };
    }
    grouped[d.client_name].totalDeductible += d.avg_lasered_deductible * d.lasered_count;
    grouped[d.client_name].totalLasered += d.lasered_count;
  }

  const data = Object.entries(grouped).map(([client, g]) => ({
    client,
    averageIncrease: g.totalLasered > 0 ? g.totalDeductible / g.totalLasered : 0,
    laseredCount: g.totalLasered,
  }));

  if (data.length === 0) return <EmptyChartState />;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.primary.main,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">Average Laser</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.warning.main,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">Lasered Claimants</Typography>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={data} layout="vertical" margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
          <CartesianGrid {...getCartesianGridProps(theme)} />
          <YAxis
            type="category"
            dataKey="client"
            width={100}
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
          />
          <XAxis
            type="number"
            xAxisId="left"
            tickFormatter={(val) => formatUSD(val, currencyCompactNotation)}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10 }}
          />
          <XAxis
            type="number"
            xAxisId="right"
            orientation="top"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10 }}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload || !payload.length) return null;
              const item = payload[0];
              const dataPoint = item.payload;
              return (
                <div
                  style={{
                    ...getChartTooltipStyle(theme),
                    padding: "8px",
                  }}
                >
                  <strong>{dataPoint.client}</strong>
                  <Divider sx={{ mb: 1, mt: 1 }} />
                  <div>Average Lasered: {formatUSD(dataPoint.averageIncrease, currencyCompactNotation)}</div>
                  <div>Lasered Claimants: {dataPoint.laseredCount}</div>
                </div>
              );
            }}
          />
          <Bar xAxisId="left" dataKey="averageIncrease" name="Average Laser" fill={theme.palette.primary.main} />
          <Line
            xAxisId="left"
            type="monotone"
            dataKey="laseredCount"
            name="Lasered Claimants"
            stroke={theme.palette.warning.main}
            strokeWidth={2}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};
